import React, { useCallback, useState } from "react";
import * as dayjs from "dayjs";
import { Button, Chip, Link } from "@material-ui/core";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "@/langs/useTranslation";
import { getProfileConfigList } from "@/apis/apiCollection";
import DataTable from "@/components/DataTable";
import HasPermission from "@/components/commons/HasPermission";
import styles from "./index.module.less";
import { useHistory } from "react-router-dom";
import { IStatus } from "../constant";

export const ProfileTemplateListDataTable = React.forwardRef(
  (
    {
      className,
      style,
      getSearchParams,
      footer,
      children,
      onCheckAll,
      onLoaded,
      onAction
    },
    ref
  ) => {
    const { t } = useTranslation();
    const history = useHistory();

    const statusName = {
      [IStatus.ENABLE]: t({ defaultMessage: "已发布" }),
      [IStatus.DISABLE]: t({ defaultMessage: "禁用" }),
      [IStatus.DRAFT]: t({ defaultMessage: "草稿" })
    };

    const columns = [
      {
        data: "id",
        type: "checkbox"
      },
      {
        data: "name",
        title: t({ defaultMessage: "模板名称" }),
        searchable: true,
        // orderable: false
        name: "1"
      },
      {
        data: "status",
        orderable: false,
        title: `${t({ defaultMessage: "模板状态" })}`,
        className: "center",
        createdCell: (td, data) => {
          const isPublished = data === 1;
          const isDisabled = data === 2;
          const ex =
            (isPublished && styles.published) ||
            (isDisabled && styles.disabled) ||
            styles.draft;
          ReactRender(
            <div className={`${styles.tag} ${ex}`}>{statusName[data]}</div>,
            td
          );
        }
      },
      {
        data: "osType",
        title: t({ defaultMessage: "类型" }),
        orderable: false
        // render: data => {
        //   switch (data) {
        //     case 0:
        //       return 'Android'
        //     case 1:
        //       return 'Windows'
        //   }
        // }
      },
      {
        data: "version",
        orderable: false,
        title: t({ defaultMessage: "发布版本" }),
        render: data => data || "-"
      },
      {
        data: "createUserName",
        // orderable: false,
        title: t({ defaultMessage: "创建人" }),
        name: "2"
      },
      {
        data: "createdAt",
        // orderable: false,
        title: t({ defaultMessage: "创建时间" }),
        name: "3",
        render: data => {
          return dayjs(Number(data)).format("YYYY-MM-DD HH:mm");
        }
      },
      {
        data: "updatedAt",
        // orderable: false,
        title: t({ defaultMessage: "最后修改时间"}),
        name: "4",
        render: data => {
          return dayjs(Number(data)).format("YYYY-MM-DD HH:mm");
        }
      },
      {
        data: "effectedAt",
        // orderable: false,
        title: t({ defaultMessage: "最后发布时间" }),
        name: "5",
        render: (data, _, row) => {
          // if (row.status === IStatus.DRAFT || row.status === IStatus.DISABLE) return "-"
          if (data === "0") return "-";
          return dayjs(Number(data)).format("YYYY/MM/DD HH:mm");
        }
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        className: "center nowrap",
        searchable: false,
        orderable: false,
        width: 220,
        createdCell: (td, data, row) => {
          ReactRender(
            <>
              <Button
                size="small"
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  onAction(e, "copy", row);
                }}
              >
                {t({ defaultMessage: "复制" })}
              </Button>
              <HasPermission permission="profile_template_edit">
                <Button
                  size="small"
                  color="primary"
                  onClick={e => onAction(e, "edit", row)}
                >
                  {t({ defaultMessage: "编辑" })}
                </Button>
              </HasPermission>
              {row.status === IStatus.DISABLE && (
                <HasPermission permission="profile_template_enable_disable">
                  <Button
                    size="small"
                    color="primary"
                    onClick={e => onAction(e, "enable", row)}
                  >
                    {t({ defaultMessage: "启用" })}
                  </Button>
                </HasPermission>
              )}
              {row.status === IStatus.ENABLE && (
                <HasPermission permission="profile_template_enable_disable">
                  <Button
                    size="small"
                    color="primary"
                    onClick={e => onAction(e, "disable", row)}
                  >
                    {t({ defaultMessage: "禁用" })}
                  </Button>
                </HasPermission>
              )}
              {row.status === IStatus.DRAFT && (
                <HasPermission permission="profile_template_edit">
                  <Button
                    size="small"
                    color="primary"
                    onClick={e => onAction(e, "release", row)}
                  >
                    {t({ defaultMessage: "发布" })}
                  </Button>
                </HasPermission>
              )}
              {row.status !== IStatus.DISABLE && (
                <HasPermission permission="profile_template_associated_with_merchant">
                  <Button
                    size="small"
                    color="primary"
                    onClick={e => onAction(e, "setGroup", row)}
                  >
                    {t({ defaultMessage: "关联门店" })}
                  </Button>
                </HasPermission>
              )}
              <HasPermission permission="profile_template_delete">
                <Button
                  size="small"
                  color="secondary"
                  onClick={e => onAction(e, "delete", row)}
                >
                  {t({ defaultMessage: "删除" })}
                </Button>
              </HasPermission>
            </>,
            td
          );
        }
      }
    ];

    const ajax = (data, callback) => {
      let orderType;
      let orderWay;
      const order = data.order[0];
      if (order && data.columns[order.column]?.orderable) {
        orderType = data.columns[order.column]?.name;
        // orderType = order.column;
        orderWay = order.dir === "asc" ? 1 : 2;
      }
      return getProfileConfigList({
        orderWay,
        orderType,
        ...getSearchParams(),
        page: data.page,
        pageSize: data.pageSize
      }).then(res => {
        callback({
          data: res.configs,
          draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    const onRowClick = useCallback((e, row) => {
      if (row && row.id) {
        if (row.osType === "android")
          history.push(`/profile-config/template-android/${row.id}`);
        if (row.osType === "windows")
          history.push(`/profile-config/template-windows/${row.id}`);
      }
    }, []);

    return (
      <>
        <DataTable
          ref={ref}
          ordering
          columns={columns}
          ajax={ajax}
          className={className}
          style={style}
          footer={footer}
          scrollable
          showCheckAll={false}
          onCheckAll={onCheckAll}
          onLoaded={onLoaded}
          onRowClick={onRowClick}
        >
          {children}
        </DataTable>
      </>
    );
  }
);
